// @ts-check

/** Above this many table cells, show the whole texts as changed instead. */
const MAX_TABLE_CELLS = 250_000

/** Words and the spaces between them, so spaces survive the diff. */
function tokenize(text) {
  return text.match(/\s+|\S+/g) ?? []
}

/**
 * Word-level diff of two strings, in reading order.
 * @param {string} oldText
 * @param {string} newText
 * @returns {Array<{ type: "same" | "removed" | "added", text: string }>}
 */
export function wordDiff(oldText, newText) {
  const a = tokenize(oldText)
  const b = tokenize(newText)
  const whole = [
    ...(oldText ? [{ type: /** @type {const} */("removed"), text: oldText }] : []),
    ...(newText ? [{ type: /** @type {const} */("added"), text: newText }] : []),
  ]
  if (a.length === 0 || b.length === 0 || (a.length + 1) * (b.length + 1) > MAX_TABLE_CELLS) return whole

  // Longest common subsequence, filled from the end so the walk below reads forwards.
  const width = b.length + 1
  const table = new Uint16Array((a.length + 1) * width)
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i * width + j] = a[i] === b[j]
        ? table[(i + 1) * width + j + 1] + 1
        : Math.max(table[(i + 1) * width + j], table[i * width + j + 1])
    }
  }

  /** @type {Array<{ type: "same" | "removed" | "added", text: string }>} */
  const parts = []
  const push = (type, text) => {
    const last = parts[parts.length - 1]
    if (last?.type === type) last.text += text
    else parts.push({ type, text })
  }

  let i = 0
  let j = 0
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      push("same", a[i])
      i++
      j++
    } else if (j >= b.length || (i < a.length && table[(i + 1) * width + j] >= table[i * width + j + 1])) {
      push("removed", a[i])
      i++
    } else {
      push("added", b[j])
      j++
    }
  }
  return parts
}
